/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, ReactElement} from 'react';
import {useSwipeable} from 'react-swipeable';
import {
  Box,
  Container,
  Collapse,
  Divider,
  Grid,
  Stack,
  Tab,
  Typography,
  Skeleton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import VideoSubjectSkeleton from 'components/VideoSubjectSkeleton';
import {TabContext, TabList, TabPanel} from '@mui/lab';
import VideoSubject from 'components/VideoSubject';
import ContextError from 'components/ContextError';
import WatchSubject from 'components/WatchSubject';
import { NoSnippet } from 'utils/NoSnippet';
// FullYtPlayer is now rendered persistently via placeholder portal
import { useVideoContextController } from 'hooks/useVideoContextController';
import { useTranslation } from 'react-i18next';
import { getVideoTitle, getVideoPromo, VideoHelmet } from 'utils/PromoUtils';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { classPath, videoPath, ownerPath } from 'routes';
import OverflowFilterBar from 'components/OverflowFilterBar';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Props for placeholder refs from Layout (optional when not provided)
interface VideoContextProps {
  desktopPlayerPlaceholderRef?: React.RefObject<HTMLDivElement>;
  mobilePlayerPlaceholderRef? : React.RefObject<HTMLDivElement>;
}

// Mobile implementation of the title-subject page
// Props for mobile implementation
interface VideoContextMobileProps {
  mobilePlayerPlaceholderRef?: React.RefObject<HTMLDivElement>;
}
const VideoContextMobile: FC<VideoContextMobileProps> = ({ mobilePlayerPlaceholderRef }): ReactElement | null => {
  const { t } = useTranslation(['videoContext', 'titleToolbar']);
  const { t: tPromo } = useTranslation('promoSubject');
  const ctrl = useVideoContextController();
  const classes = ctrl.classMetadata;
  // Hooks for theming, queue, and responsive layout
  const theme = useTheme();
  // Use placeholder ref from Layout for measuring and parking player (fallback to internal ref)
  const internalPlayerContainerRef = React.useRef<HTMLDivElement>(null);
  const playerContainerRef = mobilePlayerPlaceholderRef ?? internalPlayerContainerRef;
  const headerRef = React.useRef<HTMLDivElement>(null);
  // Estimate initial player container height (video 16:9 + top & bottom padding)
  const [playerHeight, setPlayerHeight] = React.useState<number>(() => {
    if (typeof window !== 'undefined') {
      const raw1 = theme.spacing(1);
      const raw3 = theme.spacing(3);
      // noinspection SuspiciousTypeOfGuard
      const px1 = typeof raw1 === 'string' ? parseFloat(raw1) : raw1;
      // noinspection SuspiciousTypeOfGuard
      const px3 = typeof raw3 === 'string' ? parseFloat(raw3) : raw3;
      const width = window.innerWidth - px1 * 2;
      // video height + top padding + bottom padding
      return width * (9 / 16) + px1 + px3;
    }
    return 0;
  });
  const [showHeader, setShowHeader] = React.useState(true);
  // Nav strip ref and measured height
  const navRef = React.useRef<HTMLDivElement>(null);
  const [navHeight, setNavHeight] = React.useState<number>(0);
  // Measure nav strip height for offset calculations
  React.useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height;
      if (h != null) setNavHeight(h);
    });
    ro.observe(el);
    // Initial measurement
    setNavHeight(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [classes.length]);
  // Notify persistent player to re-measure placeholder when navHeight changes
  React.useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [navHeight]);
  // Compute placeholder and sticky offsets: on mobile video pages, no AppBar above,
  // so player sits at top; tabs stick at player height
  const placeholderOffset = 0;
  const stickyTopOffset = playerHeight;
  // Observe and measure the player container height with ResizeObserver
  // Disable exhaustive-deps: playerContainerRef is stable and monitored separately
  // eslint-disable-next-line
  React.useLayoutEffect(() => {
    const el = playerContainerRef.current;
    if (!el) return;
    // Use ResizeObserver to track size changes (including initial load and window resizes)
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height;
      if (h != null) setPlayerHeight(h);
    });
    ro.observe(el);
    // Initial measurement
    setPlayerHeight(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [ctrl.watchVideo?.musicVideo]);
  // Observe header intersection to collapse
  React.useEffect(() => {
    if (!headerRef.current || playerHeight <= 0) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowHeader(entry.isIntersecting),
      { root: null, rootMargin: `-${playerHeight}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [playerHeight]);
  // Swipe between 'records' and 'similar' tabs on mobile
  const swipeHandlers = useSwipeable({
    trackTouch: true,
    preventScrollOnSwipe: false,
    trackMouse: false,
    onSwipedLeft: () => {
      if (ctrl.coverList.length > 0 && ctrl.matchList.length > 0 && ctrl.musicPanel === 0) {
        ctrl.setMusicPanel(1);
      }
    },
    onSwipedRight: () => {
      if (ctrl.coverList.length > 0 && ctrl.matchList.length > 0 && ctrl.musicPanel === 1) {
        ctrl.setMusicPanel(0);
      }
    },
  });
  
  if (ctrl.isLoading) {
    return (
      <Container disableGutters maxWidth={false} sx={{ pt: 0, position: 'relative', overflowX: 'hidden', width: '100%' }}>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Skeleton variant="rectangular" sx={{ width: '100%', height: 0, paddingTop: '56.25%' }} />
          <Skeleton width="40%" />
          <Skeleton width="30%" />
          <Divider />
          <Grid container spacing={1}>
            {Array.from({ length: 12 }).map((_, idx) => (
              <Grid key={idx} size={{ xs: 6 }}>
                <VideoSubjectSkeleton key={idx} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    );
  }
  // Error or missing video state
  if (ctrl.error || !ctrl.watchVideo) {
    const status = (ctrl.error as any)?.response?.status;
    const message = (status === 404 || !ctrl.watchVideo) ? t('notFound') : t('errorLoading');
    return <ContextError message={message} />;
  }
  // Page metadata via VideoHelmet
  const v = ctrl.watchVideo;
  const videoLingo = v.videoLingo ?? 'en'
  const videoTitle = getVideoTitle(
    tPromo,
    v.trackTitle,
    v.ownerLabel,
    v.titleCount
  );
  const videoPromo = getVideoPromo(
    tPromo,
    v.trackTitle,
    v.ownerLabel
  );
  // Determine list of videos for current panel
  return (
    <>
      <VideoHelmet
        musicVideo={v.musicVideo}
        videoTitle={videoTitle}
        videoLingo={videoLingo}
        videoPromo={videoPromo}
      />
      <Container disableGutters maxWidth={false} sx={{ pt: 0, position: 'relative', overflowX: 'hidden', width: '100%' }}>
      <Box>
        {/* Fixed-height player container */}
        {/* Fixed player: opaque container with padding */}
        {/* Placeholder for persistent player: position wrapper and aspect-ratio box */}
        <Box
          ref={playerContainerRef}
          sx={{
            position: 'fixed',
            top: `${placeholderOffset}px`,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar - 1,
            overflow: 'hidden',
            // Hide placeholder visually but keep for measurement
            visibility: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              pt: '56.25%', // maintain 16:9 aspect ratio
            }}
          />
        </Box>
          {/* Content wrapper with dynamic top margin based on App Bar + player height, plus consistent metadata spacing */}
          <Box
            sx={(theme) => ({
              // ensure header (video metadata) sits fully below the player with consistent buffer
              mt: `calc(${stickyTopOffset}px + ${theme.spacing(2.0)})`,
            })}
          >
          {/* Collapsible header (video metadata) */}
          <div ref={headerRef}>
          <Collapse in={showHeader} timeout="auto">
            <WatchSubject
              video={ctrl.watchVideo}
              titleLink={{
                to: videoPath(ctrl.watchVideo.titleVideo),
              }}
              ownerLink={{
                to: ownerPath(ctrl.watchVideo.musicOwner),
              }}
              onPlayPrevVideo={ctrl.onPlayPrevVideo}
              onPlayNextVideo={ctrl.onPlayNextVideo}
              onVideoFeedback={ctrl.onVideoFeedback}
            />
          </Collapse>
          </div>
          {(ctrl.coverList.length > 0 || ctrl.matchList.length > 0) && (
            <Box
              {...swipeHandlers}
              sx={(theme) => ({
                position: 'relative',
                // Top padding when header collapsed to make room for fixed tabs
                pt: showHeader ? 0 : theme.spacing(6),
              })}
            >
              <TabContext value={(ctrl.musicPanel === 0 && ctrl.coverList.length > 1) ? "0" : "1"}>
                {/* Tab headers */}
              <Box
                sx={(theme) => ({
                  px: 0,
                  bgcolor: theme.palette.background.paper,
                  ...(showHeader
                    ? {}
                    : {
                        position: 'fixed',
                        top: stickyTopOffset,
                        left: 0,
                        right: 0,
                        zIndex: theme.zIndex.appBar,
                      }),
                })}
              >
                <NoSnippet>
                <TabList
                  variant="fullWidth"
                  onChange={(_, val) => ctrl.setMusicPanel(+val)}
                  sx={{ px: 0 }}
                >
                  {ctrl.coverList.length > 1 && (
                    <Tab value="0" label={t('content')} />
                  )}
                  {ctrl.matchList.length > 0 && (
                    <Tab value="1" label={t('similar')} />
                  )}
                </TabList>
                </NoSnippet>
              </Box>
                {/* Tab panels below fixed tabs */}
                <TabPanel value="0" sx={{ p: 0, pt: 1, px: 0 }}>
                  <Grid container spacing={2}>
                    {ctrl.coverList.map(v => (
                      <Grid size={{ xs: 6 }} key={v.musicVideo}>
                        <VideoSubject
                          video={v}
                          videoLink={{
                            to: videoPath(v.musicVideo),
                            state: { facadeQueue: ctrl.coverList, shadowQueue: ctrl.coverList },
                          }}
                          ownerLink={{
                            to: ownerPath(v.musicOwner)
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </TabPanel>
                {/* Add top padding to match spacing of the 'content' grid */}
                <TabPanel value="1" sx={{ p: 0, pt: 1, px: 0 }}>
                  <Grid container spacing={2}>
                    {ctrl.matchList.map(v => (
                      <Grid size={{ xs: 6 }} key={v.musicVideo}>
                        <VideoSubject
                          video={v}
                          videoLink={{
                            to: videoPath(v.musicVideo),
                            state: { facadeQueue: ctrl.matchList, shadowQueue: ctrl.matchList },
                          }}
                          ownerLink={{
                            to: ownerPath(v.musicOwner)
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </TabPanel>
              </TabContext>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  </>
  );
};

// Desktop implementation of the title-subject page: embedded within Layout
interface VideoContextDesktopProps {
  desktopPlayerPlaceholderRef?: React.RefObject<HTMLDivElement>;
}
const VideoContextDesktop: FC<VideoContextDesktopProps> = ({ desktopPlayerPlaceholderRef }): ReactElement => {
  const { t } = useTranslation(['videoContext', 'titleToolbar']);
  const { t: tPromo } = useTranslation('promoSubject');
  const ctrl = useVideoContextController();
  const navigate = useNavigate();
  const classes = ctrl.classMetadata;
  // Loading skeleton state
  if (ctrl.isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', width: '100%', gap: 2, py: 2 }}>
          {/* Left skeleton: video player and details */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="rectangular" sx={{ width: '100%', height: 0, paddingTop: '56.25%' }} />
            <Skeleton width="60%" sx={{ my: 1 }} />
            <Skeleton width="40%" />
            <Divider textAlign="center" sx={{ mt: 1 }}>
              <Typography variant="button" color="text.secondary">
                <Skeleton width="20%" />
              </Typography>
            </Divider>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {Array.from({ length: 12 }).map((_, idx) => (
                <Grid key={idx} size={{ xs: 6, lg: 4 }}>
                  <VideoSubjectSkeleton key={idx} />
                </Grid>
              ))}
            </Grid>
          </Box>
          {/* Right skeleton: tabs and fab */}
          <Box sx={{ width: '320px', flexShrink: 0, pl: 2, overflowX: 'hidden' }}>
            {/* Tabs skeleton */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Skeleton variant="rectangular" width="100%" height={32} />
              <Skeleton variant="rectangular" width="100%" height={32} />
            </Box>
            {/* Video list skeleton */}
            {Array.from({ length: 4 }).map((_, idx) => (
              <Box key={idx} sx={{ mt: 1 }}>
                <VideoSubjectSkeleton key={idx} />
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    );
  }
  // Error or missing video state
  if (ctrl.error || !ctrl.watchVideo) {
    const status = (ctrl.error as any)?.response?.status;
    const message = (status === 404 || !ctrl.watchVideo) ? t('notFound') : t('errorLoading');
    return <ContextError message={message} />;
  }
  // Page metadata via VideoHelmet
  const v = ctrl.watchVideo;
  const videoLingo = v.videoLingo ?? 'en'
  const videoTitle = getVideoTitle(
    tPromo,
    v.trackTitle,
    v.ownerLabel,
    v.titleCount
  );
  const videoPromo = getVideoPromo(
    tPromo,
    v.trackTitle,
    v.ownerLabel
  );
  return (
    <>
      <VideoHelmet
        musicVideo={v.musicVideo}
        videoTitle={videoTitle}
        videoLingo={videoLingo}
        videoPromo={videoPromo}
      />
      <Container maxWidth="xl">
      <Box sx={{ display: 'flex', width: '100%', gap: 2, py: 1.5 }}>
        {/* Left: player, metadata & controls, then extra-media list */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Class category nav for Similar */}
          <NoSnippet>
          {classes.length > 0 && (
            <OverflowFilterBar
              filters={classes}
              selectedFilter={null}
              onFilterSelect={c => {
                if (c) {
                  navigate(
                    classPath(c.titleLingo, c.titleClass)
                  );
                }
              }}
              // Align filters with the player below by removing extra horizontal padding
              containerSx={{ px: 0 }}
              itemSx={{
                bgcolor: 'grey.100',
                color: 'text.primary',
                textTransform: 'uppercase',
                borderRadius: 2,
                px: 2,
                py: 1,
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.200' },
              }}
              moreSx={{
                px: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: 'grey.100',
                color: 'text.primary',
                textTransform: 'none',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.200' },
              }}
              itemIcon={<ChevronRightIcon sx={{ ml: 0.5, fontSize: '0.875rem' }} />}
            />
          )}
          </NoSnippet>
          {/* Placeholder for persistent player (hidden but used for measurement) */}
          <Box
            ref={desktopPlayerPlaceholderRef}
            sx={{
              position: 'relative',
              width: '100%',
              pt: '56.25%',
              bgcolor: 'black',
              visibility: 'hidden',
            }}
          />
          <WatchSubject
            video={ctrl.watchVideo}
            titleLink={{ to: videoPath(ctrl.watchVideo.titleVideo) }}
            ownerLink={{ to: ownerPath(ctrl.watchVideo.musicOwner) }}
            onPlayPrevVideo={ctrl.onPlayPrevVideo}
            onPlayNextVideo={ctrl.onPlayNextVideo}
            onVideoFeedback={ctrl.onVideoFeedback}
          />
          <NoSnippet>
            <Divider textAlign="center" sx={{ mt: 1 }}>
              <Typography variant="button" color="text.secondary">
                {(ctrl.musicPanel === 0 || ctrl.coverList.length <= 1)
                  ? t('similar')
                  : t('content')}
              </Typography>
            </Divider>
          </NoSnippet>
          {/* Consistent gap below divider for both 'content' and 'similar' */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {((ctrl.musicPanel === 0 || ctrl.coverList.length <= 1) ? ctrl.matchList : ctrl.coverList)
              .map((v) => (
                <Grid key={v.musicVideo} size={{ xs: 6, lg: 4 }}>
                  <VideoSubject
                    video={v}
                    videoLink={{
                      to: videoPath(v.musicVideo),
                      state: {
                        facadeQueue: (ctrl.musicPanel === 0 || ctrl.coverList.length <= 1)
                          ? ctrl.matchList
                          : ctrl.coverList,
                        shadowQueue: (ctrl.musicPanel === 0 || ctrl.coverList.length <= 1)
                          ? ctrl.matchList
                          : ctrl.coverList,
                      },
                    }}
                    ownerLink={{
                      to: ownerPath(v.musicOwner)
                    }}
                  />
                </Grid>
              ))}
          </Grid>
        </Box>
        {/* Right: tabs + selected list + reset FAB */}
        <Box sx={{ width: '320px', flexShrink: 0, pl: 2, overflowX: 'hidden' }}>
          <TabContext value={(ctrl.musicPanel === 0 && ctrl.coverList.length > 1) ? "0" : "1"}>
            <NoSnippet>
            <TabList variant="fullWidth" onChange={(_, val) => ctrl.setMusicPanel(+val)}>
              {ctrl.coverList.length > 1 && (
                <Tab
                  value="0"
                  label={t('content')}
                />
              )}
              {ctrl.matchList.length > 0 && (
                <Tab
                  value="1"
                  label={t('similar')}
                />
              )}
            </TabList>
            </NoSnippet>
            <TabPanel value="0" sx={{ p: 0, pt: 2 }}>
              <Grid container spacing={2}>
                {ctrl.coverList.map((v) => (
                  <Grid key={v.musicVideo} size={{ xs: 12 }}>
                    <VideoSubject
                      video={v}
                      videoLink={{
                        to: videoPath(v.musicVideo),
                        state: { facadeQueue: ctrl.coverList, shadowQueue: ctrl.coverList },
                      }}
                      ownerLink={{
                        to: ownerPath(v.musicOwner)
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </TabPanel>
            <TabPanel value="1" sx={{ p: 0, pt: 2 }}>
              <Grid container spacing={2}>
                {ctrl.matchList.map((v) => (
                  <Grid key={v.musicVideo} size={{ xs: 12 }}>
                    <VideoSubject
                      video={v}
                      videoLink={{
                        to: videoPath(v.musicVideo),
                        state: { facadeQueue: ctrl.matchList, shadowQueue: ctrl.matchList },
                      }}
                      ownerLink={{
                        to: ownerPath(v.musicOwner)
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </TabPanel>
          </TabContext>
        </Box>
      </Box>
    </Container>
  </>
  );
};

// Unified title-subject page: renders mobile or desktop implementation as appropriate.
const VideoContext: FC = (): ReactElement => {
  const { desktopPlayerPlaceholderRef, mobilePlayerPlaceholderRef } = useOutletContext<VideoContextProps>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Scroll to top on video route change (mobile & desktop)
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      {isMobile ? (
        <VideoContextMobile mobilePlayerPlaceholderRef={mobilePlayerPlaceholderRef} />
      ) : (
        <VideoContextDesktop desktopPlayerPlaceholderRef={desktopPlayerPlaceholderRef} />
      )}
    </>
  );
};

export default VideoContext;